import { Shield, Heart, Check, Award, Clock, Phone, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const ServiceCard = ({ service, index }) => {
  const features = service.features || [];
  return (
    <div
      id={service.id}
      className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center"
      data-testid={`service-detail-${service.id}`}
    >
      <div className={index % 2 === 1 ? "lg:order-2" : ""}>
        <img
          src={service.image}
          alt={service.name}
          className="w-full aspect-video object-cover rounded-sm border-2 border-secondary/20 rugged-shadow"
        />
      </div>
      <div className={index % 2 === 1 ? "lg:order-1" : ""}>
        <h2 className="font-heading font-bold text-2xl md:text-3xl uppercase tracking-tight text-foreground mb-4">
          {service.name}
        </h2>
        <p className="text-muted-foreground font-body mb-6">
          {service.description}
        </p>
        <ul className="space-y-3 mb-6">
          {features.map((feature, i) => (
            <li key={i} className="flex items-center gap-3">
              <Check className="h-5 w-5 text-accent flex-shrink-0" />
              <span className="text-foreground font-body">{feature}</span>
            </li>
          ))}
        </ul>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2 rounded-sm font-heading text-sm uppercase tracking-wide rugged-shadow transition-all hover:bg-primary/90"
          data-testid={`service-cta-${service.id}`}
        >
          Get Quote
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
};

const ServicesPage = () => {
  const services = [
    {
      id: "raccoons",
      name: "Raccoon Removal",
      image: "https://images.unsplash.com/photo-1682627101217-32899f5800a0?w=600",
      description: "Raccoons are one of the most common wildlife intruders in Ontario homes. They often nest in attics, chimneys, and garages, causing significant damage and health risks.",
      features: ["Safe removal from attics and chimneys", "Humane live trapping methods", "Entry point sealing and exclusion", "Damage assessment and repair"]
    },
    {
      id: "squirrels",
      name: "Squirrels and Chipmunks",
      image: "https://images.unsplash.com/photo-1615125965946-9a5ca7a488dc?w=600",
      description: "Squirrels and chipmunks can cause extensive damage by chewing through wiring, insulation, and structural components. This is a major fire hazard for homeowners.",
      features: ["Attic and soffit removal", "Wire damage prevention", "One-way exclusion doors", "Fire hazard mitigation"]
    },
    {
      id: "rodents",
      name: "Rats and Mice",
      image: "https://images.unsplash.com/photo-1425082661705-1834bfd09dca?w=600",
      description: "Rodent infestations pose serious health risks and can contaminate food supplies. Our comprehensive approach eliminates current populations and prevents future invasions.",
      features: ["Complete infestation removal", "Entry point identification", "Sanitation services", "Prevention strategies"]
    },
    {
      id: "skunks",
      name: "Skunk Removal",
      image: "https://images.unsplash.com/photo-1562023692-3dae245dbb09?w=600",
      description: "Skunks often den under porches, decks, and sheds. Professional removal is essential to avoid spray incidents that can affect your family and pets.",
      features: ["Odor-free removal techniques", "Under-deck exclusion", "Humane relocation", "Prevention barriers"]
    },
    {
      id: "snakes",
      name: "Snake Removal",
      image: "https://customer-assets.emergentagent.com/job_site-recovery-31/artifacts/99zzox2n_IMG_20250817_140355_1~2.jpg",
      description: "Snakes can find their way into homes, garages, and yards. We safely capture and relocate snakes, giving you peace of mind.",
      features: ["Safe capture techniques", "Humane relocation", "Entry point identification", "Prevention advice"]
    },
    {
      id: "birds",
      name: "Bird Control",
      image: "https://customer-assets.emergentagent.com/job_site-recovery-31/artifacts/2h668yqq_images.jpeg",
      description: "Birds nesting in vents, soffits, and chimneys can cause health hazards and structural damage. We safely remove and exclude problem birds.",
      features: ["Vent and soffit exclusion", "Chimney cap installation", "Nest removal", "Bird deterrent systems"]
    },
    {
      id: "bats",
      name: "Bat Removal",
      image: "https://customer-assets.emergentagent.com/job_site-recovery-31/artifacts/2h668yqq_images.jpeg",
      description: "Bats are protected wildlife that require specialized removal techniques. We follow all provincial guidelines for safe and legal bat exclusion.",
      features: ["Legal exclusion methods", "One-way door installation", "Guano cleanup", "Seasonal timing compliance"]
    },
    {
      id: "attic-cleanup",
      name: "Attic Cleanup and Insulation",
      image: "https://customer-assets.emergentagent.com/job_site-recovery-31/artifacts/52xuf341_unnamed.jpg",
      description: "Wildlife infestations often leave behind contaminated insulation that poses health risks. We provide complete removal and replacement services.",
      features: ["Contaminated insulation removal", "Sanitization and deodorizing", "New insulation installation", "Air quality improvement"]
    }
  ];

  return (
    <div data-testid="services-page">
      <section className="bg-primary py-16 lg:py-24" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="font-heading font-bold text-4xl md:text-5xl uppercase tracking-tight text-primary-foreground mb-6">
              Our Services
            </h1>
            <p className="text-primary-foreground/80 font-body text-lg">
              Comprehensive wildlife control solutions for residential and commercial properties. 
              We use humane live trapping methods and ensure all entry points are sealed to prevent future problems.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-secondary/10 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center gap-2 text-foreground/80">
              <Heart className="h-5 w-5 text-primary" />
              <span className="font-heading text-sm uppercase tracking-wide">Humane Methods</span>
            </div>
            <div className="flex items-center gap-2 text-foreground/80">
              <Shield className="h-5 w-5 text-primary" />
              <span className="font-heading text-sm uppercase tracking-wide">Fully Licensed</span>
            </div>
            <div className="flex items-center gap-2 text-foreground/80">
              <Award className="h-5 w-5 text-primary" />
              <span className="font-heading text-sm uppercase tracking-wide">Guaranteed Results</span>
            </div>
            <div className="flex items-center gap-2 text-foreground/80">
              <Clock className="h-5 w-5 text-primary" />
              <span className="font-heading text-sm uppercase tracking-wide">Quick Response</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 px-4" data-testid="services-list">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-16">
            {services.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-primary" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-primary-foreground mb-6">
            Need Help With Wildlife?
          </h2>
          <p className="text-primary-foreground/80 font-body text-lg mb-8">
            Contact us today for a free inspection and quote. We respond quickly to all inquiries.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-sm font-heading uppercase tracking-wide rugged-shadow transition-all hover:bg-accent/90"
              data-testid="services-cta-quote"
            >
              Request Free Quote
              <ChevronRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:343-263-2000"
              className="inline-flex items-center justify-center gap-2 border-2 border-primary-foreground text-primary-foreground px-8 py-4 rounded-sm font-heading uppercase tracking-wide transition-all hover:bg-primary-foreground hover:text-primary"
              data-testid="services-cta-call"
            >
              <Phone className="h-4 w-4" />
              343-263-2000
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
